// PortalChooser — lists only the portals this identity may enter (portal-role-permissions.md §1).
//
// Filtering here is the first of two gates. RequirePortal in App.tsx is the second, so a direct URL
// is closed too.
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { devUser } from '../lib/api'
import { portalsFor, type PortalKey } from '../lib/capabilities'
import SignIn from './SignIn'

const META: Record<PortalKey, { path: string; title: string; blurb: string }> = {
  tc:    { path: '/tc',    title: 'Town Council',    blurb: 'Call tenders, evaluate, recommend, approve, award.' },
  ams:   { path: '/ams',   title: 'Arrears Management', blurb: 'Segment residents, engage, escalate and recover — with the hardship rules applied.' },
  bid:   { path: '/bid',   title: 'Tenderer',        blurb: 'Find tenders, build a compliant submission, sign and submit.' },
  ma:    { path: '/ma',    title: 'Managing Agent',  blurb: 'Run tenders for a Town Council, under a per-tender grant.' },
  audit: { path: '/audit', title: 'Auditor',         blurb: 'Review a completed tender and its full audit trail.' },
  dev:   { path: '/dev',   title: 'Developer',       blurb: 'Templates, tenancy, prompt registry, observability.' },
}

export default function PortalChooser() {
  const { identity, me, loading, error, reload } = useAuth()

  if (loading) return <div className="page-state">Loading…</div>

  // With auth bypassed, "no account for this identity" means the configured dev user matches no
  // seeded row. That is a sign-in problem, not a failure — offer the picker rather than a dead end.
  if (error && !devUser()) return <SignIn onSignedIn={reload} />
  // Reported, never substituted with a default view (TD-034).
  if (error) {
    return (
      <div className="page-state page-state-error">
        {error}
        {/* A link to the picker, not a storage clear. Clearing it does not sign anyone out: the
            server falls back to its configured identity and signs you straight back in as that. */}
        <p><Link className="btn" to="/signin">Sign in as someone else</Link></p>
      </div>
    )
  }

  const portals = portalsFor(identity)

  // One portal and nothing else: go straight there. An Arrears Manager should land in AMS, not on
  // a chooser with a single card on it.
  const only = portals.length === 1 ? portals[0] : undefined
  if (only) return <Navigate to={META[only].path} replace />

  return (
    <div className="chooser">
      <h1>TownForge</h1>
      {me?.org && <p className="chooser-org">{me.org.name} · {me.role}</p>}
      {me && !me.authEnabled && (
        <p className="chooser-dev">
          Signed in as <b>{me.user.name}</b> (development sign-in).{' '}
          <Link className="linkish" to="/signin">Change</Link>
        </p>
      )}

      {portals.length === 0 ? (
        <p className="page-state-error">
          Your account is not associated with a portal. Ask your administrator to assign you an
          organisation and role.
        </p>
      ) : (
        <ul className="chooser-list">
          {portals.map(p => (
            <li key={p}>
              <Link to={META[p].path} className="chooser-card">
                <span className="chooser-title">{META[p].title}</span>
                <span className="chooser-blurb">{META[p].blurb}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <p className="chooser-verify">
        <Link to="/verify">Verify a signed document or an award snapshot</Link> — no account needed.
      </p>
    </div>
  )
}
